import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import config from "../Config";
import Topbar from "../Components/Topbar";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

function ServiceDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [service, setService] = useState(null);
  const [subCategories, setSubCategories] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchService = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${config.apiUrl}/api/v1/admin/service/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setService(data);
      } else {
        throw new Error("Failed to fetch service");
      }
    } catch (error) {
      toast.error(error.message);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchSubCategories = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${config.apiUrl}/api/v1/admin/subcategories?disable_pagination=true`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setSubCategories(data);
      } else {
        throw new Error("Failed to fetch subcategories");
      }
    } catch (error) {
      toast.error(error.message);
    }
  }, []);

  const fetchCategories = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${config.apiUrl}/api/v1/admin/categories?disable_pagination=true`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setCategories(data);
      } else {
        throw new Error("Failed to fetch categories");
      }
    } catch (error) {
      toast.error(error.message);
    }
  }, []);

  useEffect(() => {
    fetchService();
    fetchSubCategories();
    fetchCategories();
  }, [fetchService, fetchSubCategories, fetchCategories]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600"></div>
          <p className="mt-4 text-gray-600">Loading service details...</p>
        </div>
      </div>
    );
  }

  if (error || !service) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-red-600">Error: {error || "Service not found"}</p>
          <button
            onClick={() => navigate("/services")}
            className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
          >
            Back to Services
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Topbar />
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="container mx-auto px-4 py-8">
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate("/services")}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              ← Back to Services
            </button>
            <h1 className="text-3xl font-bold text-gray-900">Service Details</h1>
          </div>
          <button
            onClick={() => navigate(`/services/${id}`)}
            className="px-6 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
          >
            Edit Service
          </button>
          <button
            onClick={() => navigate(`/services/${id}/content-sections`)}
            className="px-6 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700"
          >
            Content sections
          </button>
        </div>

        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="p-8">
            <table className="w-full">
              <tbody className="divide-y divide-gray-200">
                <tr>
                  <td className="text-left font-black py-3 text-sm font-medium text-gray-500 w-1/3">
                    Category
                  </td>
                  <td className="text-left py-3 text-sm text-gray-900">
                    {categories.find(
                      (cat) =>
                        cat.category_id ===
                        subCategories.find(
                          (sub) =>
                            sub.sub_category_id === service.sub_category_id
                        )?.category_id
                    )?.title || "-"}
                  </td>
                </tr>
                <tr>
                  <td className="text-left font-black py-3 text-sm font-medium text-gray-500">
                    Subcategory
                  </td>
                  <td className="text-left py-3 text-sm text-gray-900">
                    {subCategories.find(
                      (sub) => sub.sub_category_id === service.sub_category_id
                    )?.title || "-"}
                  </td>
                </tr>
                {Object.entries(service).map(
                  ([key, value]) =>
                    key !== "sub_category_id" && (
                      <tr key={key}>
                        <td className="text-left font-black py-3 text-sm font-medium text-gray-500">
                          {key.charAt(0).toUpperCase() +
                            key.slice(1).replace(/_/g, " ")}
                        </td>
                        <td className="text-left py-3 text-sm text-gray-900">
                          {typeof value === "boolean" ? (
                            <span
                              className={`px-2 py-1 rounded-full text-xs ${
                                value
                                  ? "bg-green-100 text-green-800"
                                  : "bg-red-100 text-red-800"
                              }`}
                            >
                              {value ? "Yes" : "No"}
                            </span>
                          ) : Array.isArray(value) ? (
                            <div className="space-y-1">
                              {value.length === 0 ? (
                                <span className="text-gray-400">-</span>
                              ) : (
                                value.map((item, index) => (
                                  <div key={index} className="text-sm">
                                    {key === "valid_extra_charge" ? (
                                      item.replace(/_/g, " ")
                                    ) : key === "images" ? (
                                      <div className="flex items-center space-x-2 mb-2">
                                        <img
                                          src={item}
                                          alt={`Preview ${index + 1}`}
                                          className="w-16 h-16 object-cover rounded"
                                          onError={(e) => {
                                            e.target.style.display = "none";
                                          }}
                                        />
                                        <a
                                          href={item}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="text-indigo-600 hover:text-indigo-900 underline"
                                        >
                                          View Image {index + 1}
                                        </a>
                                      </div>
                                    ) : key === "videos" ? (
                                      <a
                                        href={item}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-indigo-600 hover:text-indigo-900 underline block mb-1"
                                      >
                                        Video {index + 1}
                                      </a>
                                    ) : key === "faq" ? (
                                      <div className="border border-gray-200 rounded-md p-3 mb-2">
                                        <div className="font-medium text-gray-900 mb-1">
                                          Q: {item.question || "No question"}
                                        </div>
                                        <div className="text-gray-700">
                                          A: {item.answer || "No answer"}
                                        </div>
                                      </div>
                                    ) : key === "terms_and_conditions" ? (
                                      <div className="border border-gray-200 rounded-md p-3 mb-2">
                                        <div className="text-gray-900">{item}</div>
                                      </div>
                                    ) : (
                                      <div className="mb-1">{item}</div>
                                    )}
                                  </div>
                                ))
                              )}
                            </div>
                          ) : value === null || value === "" ? (
                            <span className="text-gray-400">-</span>
                          ) : (
                            value
                          )}
                        </td>
                      </tr>
                    )
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ServiceDetails;
